import { useAuth } from '@auth/AuthProvider';
import './index.scss'
import { useCallback, useEffect, useMemo, useState } from 'react';
import EventsController, { EventAttendee, Event, Waiver, ExternalWaiver } from '@data/controller/EventsController';
import { Navigate, useParams } from 'react-router-dom';
import LoadingIndicator from '@components/LoadingIndicator';
import PageHeader from '@components/PageHeader';

import volunteerIcon from 'url:@assets/icons/volunteer-icon.svg';
import { DateTime } from 'luxon';

import { DropdownItemIcon } from '@components/Dropdown';
import WaiverReminderMailModal from './WaiverReminderMailModal';
import WaiverSetupModal from './WaiverSetupModal';
import ManualWaiverUploadModal from './ManualWaiverUploadModal';

import XLSX from "xlsx";

function useEventAttendees(eventID: string) {
  const auth = useAuth();
  const churchId = auth.churchId;

  const [event, setEvent] = useState<Event | null>(null);
  const [attendees, setAttendees] = useState<EventAttendee[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!churchId) return;

    setLoading(true);
    setError(null);

    try {
      const [eventResponse, attendeesResponse] = await Promise.all([
        EventsController.fetchEvent(churchId, eventID, auth),
        EventsController.fetchAttendeesForEvent(churchId, eventID, auth)
      ]);

      if (eventResponse.error) {
        setError(eventResponse.error);
        setAttendees(null);
        setEvent(null);
      } else if (attendeesResponse.error) {
        setError(attendeesResponse.error);
        setAttendees(null);
        setEvent(null);
      } else if (eventResponse.body && attendeesResponse.body) {
        setEvent(eventResponse.body.event);
        setAttendees(attendeesResponse.body.attendees);
      } else {
        setAttendees(null);
        setEvent(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load events');
      setAttendees(null);
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [churchId, eventID, auth]);

  const updateEvent = (newEvent: Event) => {
    setEvent(newEvent);
  }

  const updateAttendees = (newAttendees: EventAttendee[]) => {
    setAttendees(newAttendees);
  }

  useEffect(() => {
    if (!churchId || !eventID) return;

    let isMounted = true;

    const performFetch = async () => {
      await fetch();
      if (!isMounted) return;
    };

    performFetch();

    return () => {
      isMounted = false;
    };
  }, [churchId, fetch]);

  return { attendees, event, loading, error, refetch: fetch, updateEvent, updateAttendees } as const;
}

export default function EventDetailsPage() {
  const pathParams = useParams();
  const eventID = pathParams.eventID;

  if (!eventID) {
    return <Navigate to="/events" replace />;
  }

  const [showSetupWaiverModal, setShowWaiverSetupModal] = useState(false);
  const [showManualWaiverUploadModal, setShowManualWaiverUploadModal] = useState(false);
  const [showSendWaiverEmailModal, setShowWaiverEmailModal] = useState(false);
  const [waiverFilter, setWaiverFilter] = useState<'all' | 'incomplete'>('all');

  const { attendees, event, loading, error, updateEvent, updateAttendees } = useEventAttendees(eventID);

  const filteredAttendees = useMemo(() => {
    if (!attendees || !event) return [];
    return attendees.filter((attendee) => {
      if (waiverFilter === 'all') return true;
      return !event.waivers.every(waiver => attendee.completedWaivers.find(cw => cw.id === waiver.id) !== undefined);
    });
  }, [attendees, event, waiverFilter]);

  const makeWaiverCompletionsData = () => {
    if (!event || !attendees) return null;

    const headers = ['Name', ...event.waivers.map(w => w.name)];
    const rows = [headers];

    const attendeeData = attendees.sort((a, b) => {
      return a.name.localeCompare(b.name); // alphabetical order
    });

    attendeeData.forEach(attendee => {
      const row = [attendee.name];
      event.waivers.forEach(waiver => {
        const isCompleted = attendee.completedWaivers.some(cw => cw.id === waiver.id);
        row.push(isCompleted ? 'X' : '');
      });
      rows.push(row);
    });

    return rows;
  };

  return <div className='event-details-page'>

    {loading && <LoadingIndicator />}

    {error && <p className='error'>Error loading event details: {error}</p>}

    {event && <div className='content'>
      <PageHeader title={event?.title}
        backLocation='/events'
        dropdown={{
          title: 'Event Actions',
          items: [
            {
              title: 'New Waiver',
              icon: DropdownItemIcon.DOCUMENT,
              onClick: () => {
                setShowWaiverSetupModal(true);
              }
            },
            {
              title: 'Manual Waiver Upload',
              icon: DropdownItemIcon.SHARE,
              onClick: () => {
                setShowManualWaiverUploadModal(true);
              }
            },
            {
              title: 'Send Reminder Emails',
              icon: DropdownItemIcon.MAIL,
              onClick: () => {
                setShowWaiverEmailModal(true);
              }
            },
            {
              title: 'Download Completed Waivers',
              icon: DropdownItemIcon.DOCUMENT,
              onClick: () => {
                const data = makeWaiverCompletionsData();
                if (data) {
                  const worksheet = XLSX.utils.aoa_to_sheet(data);
                  const workbook = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(workbook, worksheet, "Waivers");
                  XLSX.writeFile(workbook, `${event.title}-waivers.xlsx`);
                } else {
                  window.alert('No data available to export.');
                }
              }
            }
          ]
        }}
        links={[]} />

      <WaiverSetupModal event={event}
        isOpen={showSetupWaiverModal}
        updateEvent={updateEvent}
        close={() => {
          setShowWaiverSetupModal(false);
        }} />

      <WaiverReminderMailModal event={event}
        isOpen={showSendWaiverEmailModal}
        close={() => {
          setShowWaiverEmailModal(false);
        }} />

      <ManualWaiverUploadModal event={event}
        attendees={attendees || []}
        isOpen={showManualWaiverUploadModal}
        onAttendeesUpdated={(attendees) => {
          updateAttendees(attendees);
        }}
        close={() => {
          setShowManualWaiverUploadModal(false);
        }} />

      {(attendees && event) && <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Attendees ({filteredAttendees.length})</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>Filter:</span>
            <select
              value={waiverFilter}
              onChange={(e) => setWaiverFilter(e.target.value as 'all' | 'incomplete')}
              style={{ padding: '0.25rem 0.5rem', cursor: 'pointer' }}
            >
              <option value="all">All Attendees</option>
              <option value="incomplete">Incomplete Waivers</option>
            </select>
          </label>
        </div>
        {/* TODO: Parse KSUID for event to see when it was created, if in last 10 minutes show the message below */}
        <p style={{ margin: 0 }}>If this event was just imported from Planning Center, it may take some time before attendees are available.</p>

        {attendees.length === 0 ?
          <p>No attendees registered for this event.</p> :
          <div className='legend grid-format'>
            <div aria-hidden="true" />
            <p>Name</p>
            <p>Waivers</p>
            <p></p>
            <p style={{ textAlign: 'end' }}>Submitted</p>
          </div>
        }

        <div className='cards'>
          {[...filteredAttendees].sort((a, b) => {
            const roleCompare = a.role.localeCompare(b.role);
            if (roleCompare !== 0) return roleCompare;

            const nameCompare = a.name.localeCompare(b.name);
            if (nameCompare !== 0) return nameCompare;

            return a.createdAt && b.createdAt ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() : 0;
          }).map((attendee) => (
            <AttendeeCard key={attendee.id} attendee={attendee} waivers={event.waivers} />
          ))}
        </div>
      </>}
    </div>}
  </div>
}

function AttendeeCard(props: { attendee: EventAttendee, waivers: Waiver[] }) {
  const completedAllWaivers = props.waivers.every(waiver => props.attendee.completedWaivers.find(cw => cw.id === waiver.id) !== undefined);

  return <div className={'attendee-card card grid-format ' + (completedAllWaivers ? 'complete' : 'incomplete')}>
    <div className="status-strip" aria-hidden="true" />
    <div className='metadata'>
      <p className='name'>{props.attendee.name}</p>

      <p className={'role-label ' + props.attendee.role.toLowerCase()}>
        {props.attendee.role == 'volunteer' ? <img src={volunteerIcon} className='volunteer' alt='Volunteer icon' /> : null}
        {props.attendee.role.charAt(0).toUpperCase() + props.attendee.role.slice(1)}
      </p>
    </div>

    <div className="waivers" role="group" aria-label="Waiver status">
      {props.waivers.map((waiver => {
        const completed = props.attendee.completedWaivers.find(cw => cw.id === waiver.id) !== undefined;

        return <span key={waiver.id} className={"waiver " + (completed ? "waiver--complete" : "waiver--incomplete")} aria-label={waiver.name}></span>
      }))}
    </div>
    <div></div>
    <p className='date'>{props.attendee.createdAt ? DateTime.fromISO(props.attendee.createdAt).toFormat("LLL dd, yyyy") : null}</p>
  </div>
}